import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, AlertCircle, CheckCircle, Info, RotateCcw } from 'lucide-react';
import { authApi } from '../../api';

interface Notification {
  id: string;
  type: 'refund' | 'info' | 'warning' | 'success';
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
}

export function NotificationPopup() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await authApi.getNotifications();
        if (response.notifications && response.notifications.length > 0) {
          setNotifications(response.notifications);
          setIsVisible(true);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    // Fetch notifications on mount
    fetchNotifications();
  }, []);

  const handleDismiss = async () => {
    const currentNotification = notifications[currentIndex];
    
    try {
      // Mark current notification as read
      await authApi.markNotificationsRead([currentNotification.id]);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }

    if (currentIndex < notifications.length - 1) {
      // Show next notification
      setCurrentIndex(currentIndex + 1);
    } else {
      // All notifications shown, hide popup
      setIsVisible(false);
    }
  };

  const handleDismissAll = async () => {
    try {
      // Mark all as read
      await authApi.markNotificationsRead();
      setIsVisible(false);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'refund':
        return <RotateCcw className="w-6 h-6 text-purple-400" />;
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-400" />;
      case 'info':
      default:
        return <Info className="w-6 h-6 text-blue-400" />;
    }
  };

  const getBorderColor = (type: Notification['type']) => {
    switch (type) {
      case 'refund':
        return 'border-purple-500/50';
      case 'success':
        return 'border-green-500/50';
      case 'warning':
        return 'border-yellow-500/50';
      case 'info':
      default:
        return 'border-blue-500/50';
    }
  };

  const currentNotification = notifications[currentIndex];

  if (!isVisible || !currentNotification) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && handleDismiss()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className={`relative w-full max-w-md bg-gradient-to-b from-[#1a1a2e] to-[#16161a] rounded-2xl border ${getBorderColor(currentNotification.type)} shadow-2xl overflow-hidden`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/5">
                {getIcon(currentNotification.type)}
              </div>
              <div>
                <h3 className="font-bold text-white">{currentNotification.title}</h3>
                {notifications.length > 1 && (
                  <p className="text-xs text-white/50">
                    {currentIndex + 1} of {notifications.length} notifications
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-white/80 whitespace-pre-wrap leading-relaxed">
              {currentNotification.message}
            </p>
            
            <p className="mt-4 text-xs text-white/40">
              {new Date(currentNotification.createdAt).toLocaleString()}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-4 border-t border-white/10">
            {notifications.length > 1 && (
              <button
                onClick={handleDismissAll}
                className="flex-1 py-3 px-4 rounded-xl bg-white/5 text-white/60 font-medium hover:bg-white/10 transition-colors"
              >
                Dismiss All
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-medium"
            >
              {currentIndex < notifications.length - 1 ? 'Next' : 'Got it'}
            </button>
          </div>

          {/* Bell Icon Decoration */}
          <div className="absolute -top-4 -right-4 opacity-5">
            <Bell className="w-24 h-24" />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
